// frontend/src/pages/WorkOptimizationAnalysisPage.tsx
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Row, Statistic, Table, Tag, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer } from 'recharts';
import { SessionAnalysis, getAnalysis } from '../api/workOptimization';

const { Title, Text } = Typography;

const fmtSecs = (s: number) => `${Math.floor(s / 60)}м ${Math.floor(s % 60)}с`;

const WorkOptimizationAnalysisPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [analysis, setAnalysis] = useState<SessionAnalysis | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    getAnalysis(Number(id))
      .then(setAnalysis)
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return null;
  if (!analysis) return <div>Нет данных для анализа</div>;

  const productive = analysis.category_stats.find(c => c.category === 'productive');
  const lossPercent = productive ? (100 - productive.percent).toFixed(1) : '—';

  const opColumns = [
    { title: 'Операция', dataIndex: 'name', key: 'name' },
    { title: 'Категория', dataIndex: 'category', key: 'category', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Время', key: 'time', render: (_: any, r: any) => fmtSecs(r.duration_seconds) },
    { title: '%', dataIndex: 'percent', key: 'percent', render: (v: number) => `${v}%` },
  ];

  return (
    <div>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/work-optimization')} style={{ marginBottom: 16 }}>
        Назад
      </Button>

      <Title level={4}>Анализ сессии #{id}</Title>

      {/* Summary stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card><Statistic title="Общее время" value={fmtSecs(analysis.total_seconds)} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="Полезная работа" value={`${productive?.percent ?? 0}%`} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col span={8}>
          <Card><Statistic title="Потери" value={`${lossPercent}%`} valueStyle={{ color: '#ff4d4f' }} /></Card>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Pie chart */}
        <Col xs={24} md={10}>
          <Card title="Структура времени" style={{ marginBottom: 16 }}>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={analysis.category_stats}
                  dataKey="duration_seconds"
                  nameKey="label"
                  cx="50%" cy="50%"
                  outerRadius={100}
                  label={({ label, percent }: any) => `${label} ${(percent * 100).toFixed(0)}%`}
                >
                  {analysis.category_stats.map(c => (
                    <Cell key={c.category} fill={c.color} />
                  ))}
                </Pie>
                <RTooltip formatter={(v: number) => fmtSecs(v)} />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Timeline */}
        <Col xs={24} md={14}>
          <Card title="Таймлайн" style={{ marginBottom: 16 }}>
            <div style={{ width: '100%', height: 40, display: 'flex', borderRadius: 4, overflow: 'hidden' }}>
              {analysis.timeline.map((seg, i) => (
                <Tooltip key={i} title={`${seg.name}: ${fmtSecs(seg.duration_seconds)}`}>
                  <div style={{
                    flex: seg.duration_seconds,
                    background: seg.color,
                    minWidth: 2,
                  }} />
                </Tooltip>
              ))}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {analysis.category_stats.map(c => (
                <Tag key={c.category} color={c.color}>{c.label}: {fmtSecs(c.duration_seconds)} ({c.percent}%)</Tag>
              ))}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Operations table */}
      <Card title="Детализация по операциям">
        <Table
          rowKey="name"
          dataSource={analysis.operation_stats}
          columns={opColumns}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default WorkOptimizationAnalysisPage;
